import React, {useState} from "react";
import {Dialog, DialogTitle, DialogActions, DialogContent, Stack} from "@mui/material";
import Button from "@mui/material/Button";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import TextField from "@mui/material/TextField";
import {EMAIL_REGEX} from "../../constants/regex";
import {useMutation} from "@apollo/client";
import {CREATE_USER} from "../../api/user/createUser";
import {Loader} from "../common/Loader";
import {ErrorMessage} from "../common/ErrorMessage";

type AddUserModalProps = {
    onClose: (shouldRefetch: boolean) => void,
}

export const AddUserModal = (props: AddUserModalProps) => {
    const {onClose} = props

    const [formData, setFormData] = useState<{name: string, email: string}>({name: '', email: ''})
    const [isEmailValid, setIsEmailValid] = useState(true)
    const [isSnackBarOpen, setIsSnackBarOpen] = useState(false)

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const [createUser, {loading, error}] = useMutation(CREATE_USER)

    const updateFormData = (newValues: Partial<typeof formData>) => {
        if (newValues.email !== undefined) {
            if (EMAIL_REGEX.test(newValues.email)) {
                setIsEmailValid(true)
            } else {
                setIsEmailValid(false)
            }
        }

        setFormData(oldState => ({
            ...oldState,
            ...newValues,
        }))
    }

    const onSubmit = async () => {
        const {errors} = await createUser({variables: {input: {
            name: formData.name,
            email: formData.email,
        }}})

        if (errors === undefined || errors.length === 0) {
            onClose(true)
        } else {
            setIsSnackBarOpen(true)
        }
    }

    return (
        <Dialog
            fullScreen={fullScreen}
            open={true}
            onClose={() => onClose(false)}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>
                Add New User
            </DialogTitle>

            <DialogContent>
                <Stack gap="1.5rem" paddingTop="1rem">
                    <TextField
                        label="Name"
                        onChange={(e) => updateFormData({name: e.target.value})}
                        value={formData.name}
                        variant="outlined"
                    />

                    <TextField
                        error={!isEmailValid}
                        helperText={!isEmailValid && 'Please enter valid email'}
                        label="Email"
                        onChange={(e) => updateFormData({email: e.target.value})}
                        value={formData.email}
                        variant="outlined"
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={() => onClose(false)}
                    variant="contained"
                    color="error"
                >
                    Close
                </Button>
                <Button
                    color="success"
                    disabled={!isEmailValid || formData.name === '' || formData.email === ''}
                    onClick={onSubmit}
                    variant="contained"
                >
                    Add
                </Button>
            </DialogActions>

            <Loader isVisible={loading} />

            <ErrorMessage
                handleClose={() => setIsSnackBarOpen(false)}
                isOpen={isSnackBarOpen}
                message={error?.message}
            />
        </Dialog>
    )
}
